import { useMutation, useQuery } from "@tanstack/react-query";
import { roleApiService } from "modules/Library/Role/api/role.api";
import { StudyRoleRecord } from "modules/Library/Role/helper/role.types";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

// ------------------------------------------------------------------
//-- ROLE
// ------------------------------------------------------------------

export const useRoleService = ({
  searchText,
  id,
  getDropdown,
  pageNumber = 1,
  pageSize = 10,
} :{
  searchText?: string | null;
  id ?: number | null;
  getDropdown?: boolean | null;
  pageNumber?: number;
  pageSize?: number;
} = {}) => {
  const normalizeRolePayload = (
    payload: StudyRoleRecord,
  ): Omit<StudyRoleRecord, "description"> & { description?: string } => ({
    ...payload,
    description: payload.description ?? undefined,
  });

  const SelectAllQuery = useQuery({
    queryKey: ["role-selectAll", searchText, pageNumber, pageSize],
    queryFn: () =>
      roleApiService.getSelectAllData(searchText, pageNumber, pageSize),
    enabled: !id,
  });

  const DropdownQuery = useQuery({
    queryKey: ["role-dropdown"],
    queryFn: () =>
      roleApiService.getDropdownData(),
    enabled: !!getDropdown,
  });

  const SelectQuery = useQuery({
    queryKey: ["role-select", id],
    queryFn: () => roleApiService.getSelectData(id!),
    enabled: !!id,
  });

  const InsertMutation = useMutation<ApiResponse, Error, StudyRoleRecord>({
    mutationFn: (payload: StudyRoleRecord) =>
      roleApiService.insertData(normalizeRolePayload(payload)),
  });

  const UpdateMutation = useMutation<ApiResponse, Error, StudyRoleRecord>({
    mutationFn: (payload: StudyRoleRecord) =>
      roleApiService.updateData(normalizeRolePayload(payload)),
  });

  const DeleteMutation = useMutation<
    ApiResponse,
    Error,
    { id: number | null; updatereason: string }
  >({
    mutationFn: (payload) =>
      roleApiService.deleteData({
        id: payload.id,
        updatereason: payload.updatereason,
      }),
  });

  return {
    SelectAllQuery,
    SelectQuery,
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
    DropdownQuery,
  };
};
